import {
  INSERTION_LANES,
  type CoinInsertionButtonSet,
  type CoinInsertionButtonSetOptions,
  type CoinInsertionControlEvent,
  type CoinInsertionDecision,
  type CoinInsertionDecisionDetail,
  type CoinInsertionDropEvent,
  type CoinInsertionDropOptions,
  type CoinInsertionEvent,
  type CoinInsertionEventListener,
  type CoinInsertionIntent,
  type CoinInsertionLaneButtonOptions,
  type CoinInsertionLaneState,
  type CoinInsertionLaneTelemetry,
  type CoinInsertionOptions,
  type CoinInsertionPointerOptions,
  type CoinInsertionReadiness,
  type CoinInsertionRejectReason,
  type CoinInsertionRepeatOptions,
  type CoinInsertionResetOptions,
  type CoinInsertionState,
  type CoinInsertionStateListener,
  type CoinInsertionTelemetry,
  type CoinInsertionUnbind,
  type CoinInsertionSource,
  type InsertionLane,
} from './types';

interface MutableLaneTelemetry {
  selections: number;
  attempts: number;
  accepted: number;
  rejected: number;
  lastAttemptAt: number | null;
  lastAcceptedAt: number | null;
}

interface NormalizedRepeatOptions {
  readonly enabled: boolean;
  readonly initialDelayMs: number;
  readonly intervalMs: number;
}

interface HeldDrop {
  readonly source: Exclude<CoinInsertionSource, 'api'>;
  readonly lane: InsertionLane | null;
  readonly repeat: boolean;
}

const DEFAULT_COOLDOWN_MS = 180;
const DEFAULT_REPEAT_DELAY_MS = 420;
const DEFAULT_REPEAT_INTERVAL_MS = 180;
const DROP_KEYS = new Set(['Space', 'Enter', 'NumpadEnter']);
const KEY_LANES: Readonly<Record<string, InsertionLane>> = Object.freeze({
  KeyA: 'left',
  ArrowLeft: 'left',
  KeyS: 'center',
  ArrowDown: 'center',
  ArrowUp: 'center',
  KeyD: 'right',
  ArrowRight: 'right',
});

/**
 * Desktop input/intention layer for a three-lane medal-game coin slot.
 *
 * It deliberately owns no coins or physics. `requestCoin` is the single
 * integration boundary: the host atomically spends one coin and creates or
 * queues the gameplay insertion only when it returns an accepted decision.
 */
export class CoinInsertionSystem {
  private readonly requestCoin: CoinInsertionOptions['requestCoin'];
  private readonly readBalance: CoinInsertionOptions['readBalance'];
  private readonly clock: () => number;
  private readonly onError: CoinInsertionOptions['onError'];
  private readonly initialLane: InsertionLane;
  private readonly cooldownMsValue: number;
  private readonly repeatOptions: NormalizedRepeatOptions;
  private readonly eventListeners = new Set<CoinInsertionEventListener>();
  private readonly stateListeners = new Set<CoinInsertionStateListener>();
  private readonly bindings = new Set<CoinInsertionUnbind>();
  private readonly heldDrops = new Map<string, HeldDrop>();
  private readonly laneTelemetry: Record<InsertionLane, MutableLaneTelemetry> = {
    left: createLaneTelemetry(),
    center: createLaneTelemetry(),
    right: createLaneTelemetry(),
  };

  private selectedLaneValue: InsertionLane;
  private enabledValue: boolean;
  private pausedValue: boolean;
  private disposedValue = false;
  private cooldownUntilValue = 0;
  private repeatTimer: ReturnType<typeof globalThis.setTimeout> | null = null;
  private cooldownTimer: ReturnType<typeof globalThis.setTimeout> | null = null;
  private nextIntentId = 1;
  private nextBindingId = 1;
  private startedAt: number;
  private lastEventAt: number | null = null;
  private selectionIntents = 0;
  private attempts = 0;
  private hostRequests = 0;
  private accepted = 0;
  private rejected = 0;
  private noCoinRejections = 0;
  private lastAttempt: CoinInsertionIntent | null = null;
  private lastAccepted: CoinInsertionIntent | null = null;
  private lastPublishedReadiness: CoinInsertionReadiness;
  private keyboardTarget: EventTarget | null = null;
  private blurTarget: Window | null = null;

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.defaultPrevented || isEditableTarget(event.target)) return;

    const lane = KEY_LANES[event.code];
    if (lane !== undefined) {
      if (!event.repeat && this.canCollectInput()) {
        event.preventDefault();
        this.selectLane(lane, 'keyboard');
      } else if (this.canCollectInput()) {
        event.preventDefault();
      }
      return;
    }

    if (!DROP_KEYS.has(event.code) || !this.canCollectInput()) return;
    event.preventDefault();
    if (event.repeat) return;
    this.beginHeldDrop(`keyboard:${event.code}`, {
      source: 'keyboard',
      lane: null,
      repeat: this.repeatOptions.enabled,
    });
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    if (!DROP_KEYS.has(event.code)) return;
    this.endHeldDrop(`keyboard:${event.code}`);
  };

  private readonly onWindowBlur = (): void => {
    this.clearHeldInputs();
  };

  constructor(options: CoinInsertionOptions) {
    this.requestCoin = options.requestCoin;
    this.readBalance = options.readBalance;
    this.clock = options.clock ?? defaultClock;
    this.onError = options.onError;
    this.initialLane = options.initialLane ?? 'center';
    assertLane(this.initialLane);
    this.cooldownMsValue = readNonNegative(
      options.cooldownMs ?? DEFAULT_COOLDOWN_MS,
      'cooldownMs',
    );
    this.repeatOptions = normalizeRepeatOptions(options.repeat);
    this.selectedLaneValue = this.initialLane;
    this.enabledValue = options.enabled ?? true;
    this.pausedValue = options.paused ?? false;
    this.startedAt = this.clock();
    this.lastPublishedReadiness = this.computeReadiness(this.startedAt, this.readBalanceValue());

    if (options.onEvent) this.eventListeners.add(options.onEvent);
    if (options.onStateChange) this.stateListeners.add(options.onStateChange);
    if (options.keyboard ?? true) this.installKeyboard(options.keyboardTarget);
  }

  get selectedLane(): InsertionLane {
    return this.selectedLaneValue;
  }

  get enabled(): boolean {
    return this.enabledValue;
  }

  get paused(): boolean {
    return this.pausedValue;
  }

  get disposed(): boolean {
    return this.disposedValue;
  }

  get cooldownMs(): number {
    return this.cooldownMsValue;
  }

  /** Fresh snapshot suitable for HUD/buttons and diagnostics. */
  get state(): CoinInsertionState {
    return this.getState();
  }

  /** Fresh aggregate/lane telemetry snapshot. */
  get telemetry(): CoinInsertionTelemetry {
    return this.getTelemetry();
  }

  getState(at = this.clock()): CoinInsertionState {
    const balance = this.readBalanceValue();
    const cooldownRemainingMs = this.cooldownRemaining(at);
    const readiness = this.computeReadiness(at, balance);
    const ready = readiness === 'ready';
    const lanes = {} as Record<InsertionLane, CoinInsertionLaneState>;
    for (const lane of INSERTION_LANES) {
      const telemetry = this.laneTelemetry[lane];
      lanes[lane] = Object.freeze({
        lane,
        selected: lane === this.selectedLaneValue,
        ready,
        cooldownRemainingMs,
        ...copyLaneTelemetry(telemetry),
      });
    }
    return Object.freeze({
      selectedLane: this.selectedLaneValue,
      enabled: this.enabledValue,
      paused: this.pausedValue,
      disposed: this.disposedValue,
      readiness,
      ready,
      balance,
      cost: 1 as const,
      cooldownMs: this.cooldownMsValue,
      cooldownRemainingMs,
      cooldownUntil: cooldownRemainingMs > 0 ? this.cooldownUntilValue : null,
      holding: this.heldDrops.size > 0,
      repeatEnabled: this.repeatOptions.enabled,
      repeatActive: this.repeatTimer !== null,
      lanes: Object.freeze(lanes),
    });
  }

  getTelemetry(): CoinInsertionTelemetry {
    return Object.freeze({
      startedAt: this.startedAt,
      lastEventAt: this.lastEventAt,
      selectionIntents: this.selectionIntents,
      attempts: this.attempts,
      hostRequests: this.hostRequests,
      accepted: this.accepted,
      rejected: this.rejected,
      noCoinRejections: this.noCoinRejections,
      lanes: Object.freeze({
        left: copyLaneTelemetry(this.laneTelemetry.left),
        center: copyLaneTelemetry(this.laneTelemetry.center),
        right: copyLaneTelemetry(this.laneTelemetry.right),
      }),
      lastAttempt: this.lastAttempt,
      lastAccepted: this.lastAccepted,
    });
  }

  subscribe(listener: CoinInsertionEventListener): CoinInsertionUnbind {
    this.assertActive();
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  subscribeState(listener: CoinInsertionStateListener, emitImmediately = true): CoinInsertionUnbind {
    this.assertActive();
    this.stateListeners.add(listener);
    if (emitImmediately) this.invokeSafely(() => listener(this.getState()));
    return () => this.stateListeners.delete(listener);
  }

  selectLane(lane: InsertionLane, source: CoinInsertionSource = 'api'): boolean {
    this.assertActive();
    assertLane(lane);
    if (!this.enabledValue || this.pausedValue) return false;

    const at = this.clock();
    const previousLane = this.selectedLaneValue;
    this.selectedLaneValue = lane;
    this.selectionIntents += 1;
    this.laneTelemetry[lane].selections += 1;
    this.lastEventAt = at;
    this.emit({
      type: 'lane-selected',
      lane,
      previousLane,
      changed: lane !== previousLane,
      source,
      at,
    });
    this.notifyState(at);
    return true;
  }

  /**
   * Emits one intent. On acceptance, the host callback is never called again
   * for that intent and one global cooldown begins.
   */
  requestDrop(options: CoinInsertionDropOptions = {}): CoinInsertionDropEvent {
    const lane = options.lane ?? this.selectedLaneValue;
    const source = options.source ?? 'api';
    const repeat = options.repeat ?? false;
    assertLane(lane);

    const at = this.clock();
    const balanceBefore = this.readBalanceValue();
    const intent: CoinInsertionIntent = Object.freeze({
      id: this.nextIntentId,
      lane,
      source,
      repeat,
      cost: 1 as const,
      requestedAt: at,
      balanceBefore,
    });
    this.nextIntentId += 1;
    this.recordAttempt(intent);

    const readiness = this.computeReadiness(at, balanceBefore);
    if (readiness !== 'ready') {
      return this.reject(intent, readiness, balanceBefore);
    }

    let decision: CoinInsertionDecision;
    this.hostRequests += 1;
    try {
      decision = this.requestCoin(intent);
    } catch (error) {
      this.reportError(error);
      return this.reject(intent, 'request-error', this.readBalanceValue());
    }

    let normalized: CoinInsertionDecisionDetail;
    try {
      normalized = normalizeDecision(decision);
    } catch (error) {
      this.reportError(error);
      return this.reject(intent, 'request-error', this.readBalanceValue());
    }
    const balanceAfter = normalized.balance ?? this.readBalanceValue();
    if (!normalized.accepted) {
      return this.reject(intent, normalized.reason ?? 'rejected', balanceAfter);
    }

    this.accepted += 1;
    const laneTelemetry = this.laneTelemetry[lane];
    laneTelemetry.accepted += 1;
    laneTelemetry.lastAcceptedAt = at;
    this.lastAccepted = intent;
    this.lastEventAt = at;
    this.cooldownUntilValue = this.cooldownMsValue > 0 ? at + this.cooldownMsValue : 0;
    this.armCooldownTimer(at);
    const event: CoinInsertionDropEvent = Object.freeze({
      type: 'drop-accepted',
      intent,
      balanceAfter,
      cooldownUntil: this.cooldownUntilValue || null,
    });
    this.emit(event);
    this.notifyState(at);
    return event;
  }

  enable(): void {
    this.setEnabled(true);
  }

  disable(): void {
    this.setEnabled(false);
  }

  setEnabled(enabled: boolean): void {
    this.assertActive();
    if (enabled === this.enabledValue) return;
    this.enabledValue = enabled;
    if (!enabled) this.clearHeldInputs();
    this.emitControl(enabled ? 'enabled' : 'disabled');
  }

  pause(): void {
    this.setPaused(true);
  }

  resume(): void {
    this.setPaused(false);
  }

  setPaused(paused: boolean): void {
    this.assertActive();
    if (paused === this.pausedValue) return;
    this.pausedValue = paused;
    if (paused) this.clearHeldInputs();
    this.emitControl(paused ? 'paused' : 'resumed');
  }

  /** Re-publishes live balance/cooldown readiness after external state changes. */
  refresh(): CoinInsertionState {
    this.assertActive();
    const at = this.clock();
    this.notifyState(at);
    return this.getState(at);
  }

  reset(options: CoinInsertionResetOptions = {}): CoinInsertionState {
    this.assertActive();
    this.clearHeldInputs();
    this.clearCooldownTimer();
    this.cooldownUntilValue = 0;
    this.selectedLaneValue = options.lane ?? this.initialLane;
    assertLane(this.selectedLaneValue);
    this.enabledValue = options.enabled ?? true;
    this.pausedValue = options.paused ?? false;
    if (options.clearTelemetry ?? true) this.clearTelemetry();
    this.emitControl('reset');
    return this.getState();
  }

  bindLaneButton(
    lane: InsertionLane,
    element: HTMLElement,
    options: CoinInsertionLaneButtonOptions = {},
  ): CoinInsertionUnbind {
    assertLane(lane);
    return this.bindPointerButton(element, lane, options.dropOnPress ?? false, options);
  }

  bindDropButton(
    element: HTMLElement,
    options: CoinInsertionPointerOptions = {},
  ): CoinInsertionUnbind {
    return this.bindPointerButton(element, null, true, options);
  }

  /** Convenience binder for any subset of left/center/right/drop buttons. */
  bindButtons(
    buttons: CoinInsertionButtonSet,
    options: CoinInsertionButtonSetOptions = {},
  ): CoinInsertionUnbind {
    this.assertActive();
    const unbinders: CoinInsertionUnbind[] = [];
    if (buttons.left) unbinders.push(this.bindLaneButton('left', buttons.left, options.laneButtons));
    if (buttons.center) unbinders.push(this.bindLaneButton('center', buttons.center, options.laneButtons));
    if (buttons.right) unbinders.push(this.bindLaneButton('right', buttons.right, options.laneButtons));
    if (buttons.drop) unbinders.push(this.bindDropButton(buttons.drop, options.dropButton));
    let bound = true;
    return () => {
      if (!bound) return;
      bound = false;
      for (const unbind of unbinders) unbind();
    };
  }

  dispose(): void {
    if (this.disposedValue) return;
    this.clearHeldInputs();
    this.clearCooldownTimer();
    for (const unbind of [...this.bindings]) unbind();
    this.uninstallKeyboard();
    this.disposedValue = true;
    this.notifyState(this.clock());
    this.eventListeners.clear();
    this.stateListeners.clear();
  }

  private installKeyboard(target: CoinInsertionOptions['keyboardTarget']): void {
    const fallbackWindow = typeof window === 'undefined' ? null : window;
    const keyboardTarget = target ?? fallbackWindow;
    if (!keyboardTarget) {
      throw new Error('CoinInsertionSystem keyboard controls require a browser EventTarget.');
    }
    this.keyboardTarget = keyboardTarget;
    keyboardTarget.addEventListener('keydown', this.onKeyDown as EventListener);
    keyboardTarget.addEventListener('keyup', this.onKeyUp as EventListener);
    if (fallbackWindow) {
      this.blurTarget = fallbackWindow;
      fallbackWindow.addEventListener('blur', this.onWindowBlur);
    }
  }

  private uninstallKeyboard(): void {
    this.keyboardTarget?.removeEventListener('keydown', this.onKeyDown as EventListener);
    this.keyboardTarget?.removeEventListener('keyup', this.onKeyUp as EventListener);
    this.blurTarget?.removeEventListener('blur', this.onWindowBlur);
    this.keyboardTarget = null;
    this.blurTarget = null;
  }

  private bindPointerButton(
    element: HTMLElement,
    lane: InsertionLane | null,
    drops: boolean,
    options: CoinInsertionPointerOptions,
  ): CoinInsertionUnbind {
    this.assertActive();
    const bindingId = this.nextBindingId;
    this.nextBindingId += 1;
    const preventDefault = options.preventDefault ?? true;
    const repeats = options.holdToRepeat ?? this.repeatOptions.enabled;
    const activePointers = new Set<number>();
    const tokenFor = (pointerId: number): string => `pointer:${bindingId}:${pointerId}`;

    const onPointerDown = (event: PointerEvent): void => {
      if (event.button !== 0 || !event.isPrimary) return;
      if (preventDefault) event.preventDefault();
      activePointers.add(event.pointerId);
      try {
        element.setPointerCapture(event.pointerId);
      } catch {
        // Synthetic pointer events may not expose pointer capture.
      }
      if (lane !== null) this.selectLane(lane, 'pointer');
      if (drops) {
        this.beginHeldDrop(tokenFor(event.pointerId), {
          source: 'pointer',
          lane,
          repeat: repeats,
        });
      }
    };

    const onPointerEnd = (event: PointerEvent): void => {
      if (!activePointers.delete(event.pointerId)) return;
      if (preventDefault && event.type !== 'lostpointercapture') event.preventDefault();
      this.endHeldDrop(tokenFor(event.pointerId));
    };

    element.addEventListener('pointerdown', onPointerDown);
    element.addEventListener('pointerup', onPointerEnd);
    element.addEventListener('pointercancel', onPointerEnd);
    element.addEventListener('pointerleave', onPointerEnd);
    element.addEventListener('lostpointercapture', onPointerEnd);

    let bound = true;
    const unbind = (): void => {
      if (!bound) return;
      bound = false;
      element.removeEventListener('pointerdown', onPointerDown);
      element.removeEventListener('pointerup', onPointerEnd);
      element.removeEventListener('pointercancel', onPointerEnd);
      element.removeEventListener('pointerleave', onPointerEnd);
      element.removeEventListener('lostpointercapture', onPointerEnd);
      for (const pointerId of activePointers) this.endHeldDrop(tokenFor(pointerId));
      activePointers.clear();
      this.bindings.delete(unbind);
    };
    this.bindings.add(unbind);
    return unbind;
  }

  private beginHeldDrop(token: string, heldDrop: HeldDrop): void {
    if (this.heldDrops.has(token)) return;
    const wasEmpty = this.heldDrops.size === 0;
    this.heldDrops.set(token, heldDrop);
    if (!wasEmpty) {
      if (heldDrop.repeat && this.repeatTimer === null) {
        this.scheduleRepeat(this.repeatOptions.initialDelayMs);
      }
      this.notifyState(this.clock());
      return;
    }

    const result = this.requestDrop({
      lane: heldDrop.lane ?? undefined,
      source: heldDrop.source,
      repeat: false,
    });
    if (heldDrop.repeat && this.shouldContinueRepeat(result)) {
      this.scheduleRepeat(this.repeatOptions.initialDelayMs);
    }
    this.notifyState(this.clock());
  }

  private endHeldDrop(token: string): void {
    if (!this.heldDrops.delete(token)) return;
    if (this.heldDrops.size === 0) this.clearRepeatTimer();
    this.notifyState(this.clock());
  }

  private scheduleRepeat(delayMs: number): void {
    this.clearRepeatTimer();
    if (!this.hasRepeatableHold() || !this.canCollectInput()) return;
    this.repeatTimer = globalThis.setTimeout(() => {
      this.repeatTimer = null;
      const heldDrop = this.firstRepeatableHold();
      if (!heldDrop || !this.canCollectInput()) {
        this.notifyState(this.clock());
        return;
      }
      const result = this.requestDrop({
        lane: heldDrop.lane ?? undefined,
        source: heldDrop.source,
        repeat: true,
      });
      if (this.shouldContinueRepeat(result)) {
        const at = this.clock();
        this.scheduleRepeat(Math.max(this.repeatOptions.intervalMs, this.cooldownRemaining(at)));
      }
    }, delayMs);
  }

  private shouldContinueRepeat(event: CoinInsertionDropEvent): boolean {
    if (event.type === 'drop-accepted') return true;
    return event.reason === 'cooldown' || event.reason === 'rejected';
  }

  private firstRepeatableHold(): HeldDrop | null {
    for (const heldDrop of this.heldDrops.values()) {
      if (heldDrop.repeat) return heldDrop;
    }
    return null;
  }

  private hasRepeatableHold(): boolean {
    return this.firstRepeatableHold() !== null;
  }

  private clearHeldInputs(): void {
    if (this.heldDrops.size === 0 && this.repeatTimer === null) return;
    this.heldDrops.clear();
    this.clearRepeatTimer();
    if (!this.disposedValue) this.notifyState(this.clock());
  }

  private clearRepeatTimer(): void {
    if (this.repeatTimer === null) return;
    globalThis.clearTimeout(this.repeatTimer);
    this.repeatTimer = null;
  }

  private armCooldownTimer(at: number): void {
    this.clearCooldownTimer();
    const remaining = this.cooldownRemaining(at);
    if (remaining <= 0) return;
    this.cooldownTimer = globalThis.setTimeout(() => {
      this.cooldownTimer = null;
      if (this.disposedValue) return;
      const now = this.clock();
      if (this.cooldownRemaining(now) > 0) {
        this.armCooldownTimer(now);
        return;
      }
      this.cooldownUntilValue = 0;
      this.notifyState(now);
    }, remaining);
  }

  private clearCooldownTimer(): void {
    if (this.cooldownTimer === null) return;
    globalThis.clearTimeout(this.cooldownTimer);
    this.cooldownTimer = null;
  }

  private cooldownRemaining(at: number): number {
    return Math.max(0, this.cooldownUntilValue - at);
  }

  private computeReadiness(at: number, balance: number | null): CoinInsertionReadiness {
    if (this.disposedValue) return 'disposed';
    if (!this.enabledValue) return 'disabled';
    if (this.pausedValue) return 'paused';
    if (balance !== null && balance < 1) return 'no-coins';
    if (this.cooldownRemaining(at) > 0) return 'cooldown';
    return 'ready';
  }

  private readBalanceValue(): number | null {
    if (!this.readBalance) return null;
    try {
      const balance = this.readBalance();
      if (!Number.isFinite(balance) || balance < 0) {
        throw new RangeError('readBalance() must return a finite non-negative number.');
      }
      return balance;
    } catch (error) {
      this.reportError(error);
      return null;
    }
  }

  private recordAttempt(intent: CoinInsertionIntent): void {
    this.attempts += 1;
    this.lastAttempt = intent;
    const laneTelemetry = this.laneTelemetry[intent.lane];
    laneTelemetry.attempts += 1;
    laneTelemetry.lastAttemptAt = intent.requestedAt;
  }

  private reject(
    intent: CoinInsertionIntent,
    reason: CoinInsertionRejectReason,
    balanceAfter: number | null,
  ): CoinInsertionDropEvent {
    this.rejected += 1;
    this.laneTelemetry[intent.lane].rejected += 1;
    if (reason === 'no-coins') this.noCoinRejections += 1;
    this.lastEventAt = intent.requestedAt;
    const event: CoinInsertionDropEvent = Object.freeze({
      type: 'drop-rejected',
      intent,
      reason,
      balanceAfter,
    });
    this.emit(event);
    this.notifyState(intent.requestedAt);
    return event;
  }

  private clearTelemetry(): void {
    this.startedAt = this.clock();
    this.lastEventAt = null;
    this.selectionIntents = 0;
    this.attempts = 0;
    this.hostRequests = 0;
    this.accepted = 0;
    this.rejected = 0;
    this.noCoinRejections = 0;
    this.lastAttempt = null;
    this.lastAccepted = null;
    this.nextIntentId = 1;
    for (const lane of INSERTION_LANES) resetLaneTelemetry(this.laneTelemetry[lane]);
  }

  private emitControl(action: CoinInsertionControlEvent['action']): void {
    const at = this.clock();
    this.lastEventAt = at;
    this.emit(Object.freeze({ type: 'control-changed', action, at }));
    this.notifyState(at);
  }

  private notifyState(at: number): void {
    const state = this.getState(at);
    if (state.readiness !== this.lastPublishedReadiness) {
      const previous = this.lastPublishedReadiness;
      this.lastPublishedReadiness = state.readiness;
      this.emit(Object.freeze({
        type: 'readiness-changed',
        previous,
        readiness: state.readiness,
        at,
      }));
    }
    for (const listener of [...this.stateListeners]) {
      this.invokeSafely(() => listener(state));
    }
  }

  private emit(event: CoinInsertionEvent): void {
    for (const listener of [...this.eventListeners]) {
      this.invokeSafely(() => listener(event));
    }
  }

  private invokeSafely(callback: () => void): void {
    try {
      callback();
    } catch (error) {
      this.reportError(error);
    }
  }

  private reportError(error: unknown): void {
    if (!this.onError) return;
    try {
      this.onError(error);
    } catch {
      // Error observers must not destabilize held-input cleanup.
    }
  }

  private canCollectInput(): boolean {
    return !this.disposedValue && this.enabledValue && !this.pausedValue;
  }

  private assertActive(): void {
    if (this.disposedValue) throw new Error('CoinInsertionSystem has been disposed.');
  }
}

export function createCoinInsertionSystem(options: CoinInsertionOptions): CoinInsertionSystem {
  return new CoinInsertionSystem(options);
}

function createLaneTelemetry(): MutableLaneTelemetry {
  return {
    selections: 0,
    attempts: 0,
    accepted: 0,
    rejected: 0,
    lastAttemptAt: null,
    lastAcceptedAt: null,
  };
}

function resetLaneTelemetry(telemetry: MutableLaneTelemetry): void {
  telemetry.selections = 0;
  telemetry.attempts = 0;
  telemetry.accepted = 0;
  telemetry.rejected = 0;
  telemetry.lastAttemptAt = null;
  telemetry.lastAcceptedAt = null;
}

function copyLaneTelemetry(telemetry: MutableLaneTelemetry): CoinInsertionLaneTelemetry {
  return Object.freeze({
    selections: telemetry.selections,
    attempts: telemetry.attempts,
    accepted: telemetry.accepted,
    rejected: telemetry.rejected,
    lastAttemptAt: telemetry.lastAttemptAt,
    lastAcceptedAt: telemetry.lastAcceptedAt,
  });
}

function normalizeRepeatOptions(
  options: boolean | CoinInsertionRepeatOptions | undefined,
): NormalizedRepeatOptions {
  const detail = typeof options === 'object' ? options : undefined;
  return Object.freeze({
    enabled: typeof options === 'boolean' ? options : detail?.enabled ?? false,
    initialDelayMs: readNonNegative(
      detail?.initialDelayMs ?? DEFAULT_REPEAT_DELAY_MS,
      'repeat.initialDelayMs',
    ),
    intervalMs: readPositive(
      detail?.intervalMs ?? DEFAULT_REPEAT_INTERVAL_MS,
      'repeat.intervalMs',
    ),
  });
}

function normalizeDecision(decision: CoinInsertionDecision): CoinInsertionDecisionDetail {
  if (typeof decision === 'boolean') return { accepted: decision };
  const balance = decision.balance;
  if (balance !== undefined && (!Number.isFinite(balance) || balance < 0)) {
    throw new RangeError('A coin insertion decision balance must be finite and non-negative.');
  }
  return decision;
}

function assertLane(lane: InsertionLane): void {
  if (!INSERTION_LANES.includes(lane)) {
    throw new RangeError(`Unknown insertion lane: ${String(lane)}.`);
  }
}

function readNonNegative(value: number, label: string): number {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${label} must be a finite non-negative number.`);
  }
  return value;
}

function readPositive(value: number, label: string): number {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${label} must be a finite number greater than zero.`);
  }
  return value;
}

function defaultClock(): number {
  return performance.now();
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tagName = target.tagName;
  return tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
}
